import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { StockDiscrepancy, StockDiscrepancyCreateRequest, StockDiscrepancyAutoRequest, StockDiscrepancyReport, StockDiscrepancyStats } from '../Module/StockDiscrepancy.model';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class StockDiscrepancyService {
  private get apiUrl(): string {
    return `${environment.apiUrl}/stock-discrepancies`;
  }

  constructor(private http: HttpClient) { }

  // Créer un nouvel écart de stock
  createStockDiscrepancy(request: StockDiscrepancyCreateRequest): Observable<StockDiscrepancy> {
    const params = new HttpParams()
      .set('productId', request.productId.toString())
      .set('realQuantity', request.realQuantity.toString())
      .set('systemQuantity', request.systemQuantity.toString())
      .set('comment', request.comment || '');

    return this.http.post<StockDiscrepancy>(this.apiUrl, null, { params });
  }

  // Créer automatiquement un écart de stock
  createAutomaticStockDiscrepancy(request: StockDiscrepancyAutoRequest): Observable<StockDiscrepancy> {
    let params = new HttpParams()
      .set('productId', request.productId.toString())
      .set('realQuantity', request.realQuantity.toString());
    
    // Only add comment if it's not empty
    if (request.comment && request.comment.trim() !== '') {
      params = params.set('comment', request.comment);
    }
    
    return this.http.post<StockDiscrepancy>(`${this.apiUrl}/auto`, null, { params });
  }

  // Obtenir tous les écarts de stock
  getAllStockDiscrepancies(): Observable<StockDiscrepancy[]> {
    return this.http.get<StockDiscrepancy[]>(this.apiUrl);
  }

  // Obtenir les écarts de stock paginés
  getPaginatedStockDiscrepancies(
    page: number, 
    size: number,
    status?: string,
    productId?: number,
    date?: string
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (status && status !== 'all') {
      params = params.set('status', status);
    }
    if (productId && productId !== 0) {
      params = params.set('productId', productId.toString());
    }
    if (date) {
      params = params.set('date', date);
    }

    return this.http.get<any>(`${this.apiUrl}/paginated`, { params });
  }

  // Obtenir un écart par ID
  getStockDiscrepancyById(id: number): Observable<StockDiscrepancy> {
    return this.http.get<StockDiscrepancy>(`${this.apiUrl}/${id}`);
  }

  // Obtenir les écarts pour un produit
  getStockDiscrepanciesByProduct(productId: number): Observable<StockDiscrepancy[]> {
    return this.http.get<StockDiscrepancy[]>(`${this.apiUrl}/product/${productId}`);
  }

  // Obtenir les écarts en attente d'approbation
  getPendingStockDiscrepancies(): Observable<StockDiscrepancy[]> {
    return this.http.get<StockDiscrepancy[]>(`${this.apiUrl}/pending`);
  }

  // Obtenir les écarts approuvés mais non appliqués
  getApprovedNotAppliedStockDiscrepancies(): Observable<StockDiscrepancy[]> {
    return this.http.get<StockDiscrepancy[]>(`${this.apiUrl}/approved-not-applied`);
  }

  // Approuver un écart
  approveStockDiscrepancy(id: number): Observable<StockDiscrepancy> {
    return this.http.post<StockDiscrepancy>(`${this.apiUrl}/${id}/approve`, {});
  }

  // Rejeter un écart
  rejectStockDiscrepancy(id: number): Observable<StockDiscrepancy> {
    return this.http.post<StockDiscrepancy>(`${this.apiUrl}/${id}/reject`, {});
  }

  // Supprimer un écart
  deleteStockDiscrepancy(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`);
  }

  // Mettre à jour un écart
  updateStockDiscrepancy(id: number, realQuantity?: number, systemQuantity?: number, comment?: string): Observable<StockDiscrepancy> {
    let params = new HttpParams();
    if (realQuantity !== undefined) params = params.set('realQuantity', realQuantity.toString());
    if (systemQuantity !== undefined) params = params.set('systemQuantity', systemQuantity.toString());
    if (comment !== undefined) params = params.set('comment', comment);

    return this.http.put<StockDiscrepancy>(`${this.apiUrl}/${id}`, null, { params });
  }

  // Vérifier s'il y a des écarts non appliqués pour un produit
  checkUnappliedDiscrepancies(productId: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/check-unapplied/${productId}`);
  }
  


  // Générer un rapport
  generateReport(startDate: string, endDate: string): Observable<StockDiscrepancy[]> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http.get<StockDiscrepancy[]>(`${this.apiUrl}/report`, { params });
  }

  // Calculer le total des écarts approuvés
  calculateTotalApproved(startDate: string, endDate: string): Observable<number> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http.get<number>(`${this.apiUrl}/total-approved`, { params });
  }

  // Calculer les statistiques
  calculateStats(discrepancies: StockDiscrepancy[]): StockDiscrepancyStats {
    const stats: StockDiscrepancyStats = {
      totalDiscrepancies: discrepancies.length,
      pendingDiscrepancies: 0,
      approvedDiscrepancies: 0,
      rejectedDiscrepancies: 0,
      appliedDiscrepancies: 0,
      totalQuantity: 0,
      positiveQuantity: 0,
      negativeQuantity: 0
    };

    discrepancies.forEach(discrepancy => {
      switch (discrepancy.status) {
        case 'En attente':
          stats.pendingDiscrepancies++;
          break;
        case 'Approuvé':
          stats.approvedDiscrepancies++;
          break;
        case 'Rejeté':
          stats.rejectedDiscrepancies++;
          break;
      }

      if (discrepancy.adjustmentApplied) {
        stats.appliedDiscrepancies++;
      }

      stats.totalQuantity += discrepancy.discrepancyQuantity;
      if (discrepancy.discrepancyQuantity > 0) {
        stats.positiveQuantity += discrepancy.discrepancyQuantity;
      } else {
        stats.negativeQuantity += Math.abs(discrepancy.discrepancyQuantity);
      }
    });

    return stats;
  }

  // Formater la date pour l'affichage
  formatDate(dateString: string): string {
    if (!dateString) {
      return 'N/A';
    }
    
    try {
      const date = new Date(dateString);
      
      // Vérifier si la date est valide
      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }
      
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', dateString, error);
      return 'Date invalide';
    }
  }

  // Formater la quantité pour l'affichage
  formatQuantity(quantity: number): string {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(quantity);
  }

  // Obtenir la classe CSS pour le statut
  getStatusClass(status: string): string {
    switch (status) {
      case 'En attente':
        return 'text-yellow-600 bg-yellow-100';
      case 'Approuvé':
        return 'text-green-600 bg-green-100';
      case 'Rejeté':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  }

  // Obtenir la classe CSS pour la quantité d'écart
  getDiscrepancyClass(quantity: number): string {
    if (quantity > 0) {
      return 'text-green-600';
    } else if (quantity < 0) {
      return 'text-red-600';
    } else {
      return 'text-gray-600';
    }
  }

  // Obtenir la classe CSS pour l'état d'application
  getAppliedClass(applied: boolean): string {
    return applied ? 'text-green-600 bg-green-100' : 'text-yellow-600 bg-yellow-100';
  }
} 